import { create } from 'zustand';
import type { Lang } from '@/i18n/translations';

export interface TestResult {
  score: number;
  primary: string;
  details: Record<string, unknown>;
}

export type DeviceMode = 'tv' | 'mobile';

export const TV_TESTS = [
  'tv-list', 'tv-css', 'tv-compute', 'tv-memory',
  'tv-canvas2d', 'tv-webgl', 'tv-events', 'tv-network',
] as const;

export const MOBILE_TESTS = [
  'mobile-touch', 'mobile-viewport', 'mobile-scroll', 'mobile-media',
  'mobile-battery', 'mobile-pwa', 'mobile-form', 'mobile-network',
] as const;

export type TvTestId = typeof TV_TESTS[number];
export type MobileTestId = typeof MOBILE_TESTS[number];
export type TestId = TvTestId | MobileTestId;

export const TV_WEIGHTS: Record<TvTestId, number> = {
  'tv-list': 0.15,
  'tv-css': 0.15,
  'tv-compute': 0.15,
  'tv-memory': 0.10,
  'tv-canvas2d': 0.10,
  'tv-webgl': 0.15,
  'tv-events': 0.10,
  'tv-network': 0.10,
};

export const MOBILE_WEIGHTS: Record<MobileTestId, number> = {
  'mobile-touch': 0.20,
  'mobile-viewport': 0.10,
  'mobile-scroll': 0.20,
  'mobile-media': 0.10,
  'mobile-battery': 0.10,
  'mobile-pwa': 0.10,
  'mobile-form': 0.10,
  'mobile-network': 0.10,
};

interface TestStore {
  language: Lang;
  deviceMode: DeviceMode;
  activeView: string;
  results: Partial<Record<TestId, TestResult>>;
  autoRunning: boolean;
  autoIdx: number;
  autoNonce: number;

  setLanguage: (lang: Lang) => void;
  setDeviceMode: (mode: DeviceMode) => void;
  setActiveView: (view: string) => void;
  reportResult: (testId: TestId, result: TestResult) => void;
  startAutoRun: () => void;
  onAutoDone: (testId: TestId) => void;
  resetAll: () => void;

  getRunOrder: () => readonly string[];
  getAutoProgress: () => string;
  getOverallScore: () => number | null;
  getVerdict: () => string;
}

export const useTestStore = create<TestStore>((set, get) => ({
  language: 'pl',
  deviceMode: 'tv',
  activeView: 'overview',
  results: {},
  autoRunning: false,
  autoIdx: 0,
  autoNonce: 0,

  setLanguage: (lang) => set({ language: lang }),

  setDeviceMode: (mode) => set({
    deviceMode: mode,
    activeView: 'overview',
    results: {},
    autoRunning: false,
    autoIdx: 0,
  }),

  setActiveView: (view) => {
    if (!get().autoRunning) set({ activeView: view });
  },

  reportResult: (testId, result) => set((state) => ({
    results: { ...state.results, [testId]: result },
  })),

  startAutoRun: () => {
    const order = get().getRunOrder();
    set({
      results: {},
      autoRunning: true,
      autoIdx: 0,
      autoNonce: get().autoNonce + 1,
      activeView: order[0],
    });
  },

  onAutoDone: (_testId) => {
    const state = get();
    const order = state.getRunOrder();
    const next = state.autoIdx + 1;
    if (next >= order.length) {
      set({ autoRunning: false, activeView: 'summary' });
    } else {
      set({ autoIdx: next, activeView: order[next] });
    }
  },

  resetAll: () => set({
    results: {},
    autoRunning: false,
    autoIdx: 0,
    activeView: 'overview',
  }),

  getRunOrder: () => {
    const mode = get().deviceMode;
    return mode === 'tv' ? TV_TESTS : MOBILE_TESTS;
  },

  getAutoProgress: () => {
    const state = get();
    const order = state.getRunOrder();
    return `${state.autoIdx + 1}/${order.length}`;
  },

  getOverallScore: () => {
    const state = get();
    const mode = state.deviceMode;
    const weights = mode === 'tv' ? TV_WEIGHTS : MOBILE_WEIGHTS;
    const tests = mode === 'tv' ? TV_TESTS : MOBILE_TESTS;

    let totalWeight = 0;
    let weightedSum = 0;

    for (const testId of tests) {
      const result = state.results[testId];
      if (result && typeof result.score === 'number') {
        const w = weights[testId as keyof typeof weights];
        weightedSum += result.score * w;
        totalWeight += w;
      }
    }

    if (totalWeight === 0) return null;
    return Math.round(weightedSum / totalWeight);
  },

  getVerdict: () => {
    const score = get().getOverallScore();
    if (score === null) return 'verdict.noData';
    if (score >= 85) return 'verdict.excellent';
    if (score >= 70) return 'verdict.good';
    if (score >= 55) return 'verdict.acceptable';
    return 'verdict.poor';
  },
}));
